<template>
  <div
    class="ds-field"
    :class="{ stacked }"
  >
    <div class="ds-field-info">
      <label
        class="ds-field-label"
        :for="labelFor"
      >{{ label }}</label>
      <p
        v-if="hint"
        class="ds-field-hint"
      >
        {{ hint }}
      </p>
    </div>
    <div
      class="ds-field-control"
      :class="width"
    >
      <!-- describedBy links the control to the message below; bind it as aria-describedby
           so a screen reader reads the error, not just the label. -->
      <slot
        :described-by="error ? errorId : null"
        :invalid="!!error"
      />
      <div
        v-if="error"
        :id="errorId"
        class="ds-field-error"
        role="alert"
      >
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
let uid = 0

// Label + hint beside its control. `stacked` puts the control underneath instead, for
// controls that need the full width (textareas, images).
export default {
  name: 'DsField',
  props: {
    label: {
      type: String,
      required: true,
    },
    hint: {
      type: String,
      default: '',
    },
    // id of the control the label points at.
    labelFor: {
      type: String,
      default: null,
    },
    error: {
      type: String,
      default: '',
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    // Caps the control so a postal code doesn't stretch the whole card.
    width: {
      type: String,
      default: 'full',
      validator: value => ['sm', 'md', 'lg', 'full'].includes(value),
    },
  },
  data() {
    uid += 1
    return { fallbackId: `ds-field-${uid}` }
  },
  computed: {
    errorId() {
      return this.labelFor ? `${this.labelFor}-error` : `${this.fallbackId}-error`
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

.ds-field {
  display: grid;
  grid-template-columns: minmax(0, 240px) minmax(0, 1fr);
  gap: 8px 28px;
  align-items: start;
  padding: 16px 0;
  border-top: 1px solid $c-border-light;

  &:first-child {
    padding-top: 0;
    border-top: none;
  }

  &.stacked {
    grid-template-columns: minmax(0, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ds-field-info {
  min-width: 0;
  padding-top: 8px;

  .ds-field.stacked & {
    padding-top: 0;
  }
}

.ds-field-label {
  display: block;
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: $c-text;
}

.ds-field-hint {
  margin: 3px 0 0;
  font-size: 11.5px;
  line-height: 1.5;
  color: $c-text-sec;
}

.ds-field-control {
  min-width: 0;

  &.sm { max-width: 160px; }

  &.md { max-width: 280px; }

  &.lg { max-width: 420px; }
}

.ds-field-error {
  @include ds-error-text;
}
</style>
